var _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; };

import React from 'react';
import PropTypes from 'prop-types';
import { FormattedDate, FormattedMessage } from 'react-intl';

import ClassificationProperty from '../classification/ClassificationProperty';

import EditableDescription from './EditableDescription';
import EditableURL from './EditableURL';
import RetentionPolicy from './RetentionPolicy';
import ReadonlyDescription from './ReadonlyDescription';
import messages from './messages';

import './ItemProperties.scss';

var datetimeOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric'
};

var ItemProperties = function ItemProperties(_ref) {
    var _ref$classificationPr = _ref.classificationProps,
        classificationProps = _ref$classificationPr === undefined ? {} : _ref$classificationPr,
        createdAt = _ref.createdAt,
        description = _ref.description,
        _ref$descriptionTexta = _ref.descriptionTextareaProps,
        descriptionTextareaProps = _ref$descriptionTexta === undefined ? {} : _ref$descriptionTexta,
        enterpriseOwner = _ref.enterpriseOwner,
        modifiedAt = _ref.modifiedAt,
        onDescriptionChange = _ref.onDescriptionChange,
        onValidURLChange = _ref.onValidURLChange,
        owner = _ref.owner,
        _ref$retentionPolicyP = _ref.retentionPolicyProps,
        retentionPolicyProps = _ref$retentionPolicyP === undefined ? {} : _ref$retentionPolicyP,
        size = _ref.size,
        trashedAt = _ref.trashedAt,
        uploader = _ref.uploader,
        url = _ref.url;
    return React.createElement(
        'dl',
        { className: 'item-properties' },
        description || onDescriptionChange ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.description)),
            React.createElement(
                'dd',
                null,
                onDescriptionChange ? React.createElement(EditableDescription, {
                    onDescriptionChange: onDescriptionChange,
                    textAreaProps: descriptionTextareaProps,
                    value: description
                }) : React.createElement(ReadonlyDescription, { value: description })
            )
        ) : null,
        !!url && React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.url)),
            React.createElement(
                'dd',
                null,
                onValidURLChange ? React.createElement(EditableURL, {
                    onValidURLChange: onValidURLChange,
                    value: url
                }) : url
            )
        ),
        owner ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.owner)),
            React.createElement(
                'dd',
                null,
                owner
            )
        ) : null,
        enterpriseOwner ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.enterpriseOwner)),
            React.createElement(
                'dd',
                null,
                enterpriseOwner
            )
        ) : null,
        uploader ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.uploader)),
            React.createElement(
                'dd',
                null,
                uploader
            )
        ) : null,
        createdAt ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.created)),
            React.createElement(
                'dd',
                null,
                React.createElement(FormattedDate, _extends({
                    value: new Date(createdAt)
                }, datetimeOptions))
            )
        ) : null,
        modifiedAt ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.modified)),
            React.createElement(
                'dd',
                null,
                React.createElement(FormattedDate, _extends({
                    value: new Date(modifiedAt)
                }, datetimeOptions))
            )
        ) : null,
        size ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.size)),
            React.createElement(
                'dd',
                null,
                size
            )
        ) : null,
        trashedAt ? React.createElement(
            React.Fragment,
            null,
            React.createElement(FormattedMessage, _extends({ tagName: 'dt' }, messages.deleted)),
            React.createElement(
                'dd',
                null,
                React.createElement(FormattedDate, _extends({
                    value: new Date(trashedAt)
                }, datetimeOptions))
            )
        ) : null,
        React.createElement(RetentionPolicy, retentionPolicyProps),
        React.createElement(ClassificationProperty, classificationProps)
    );
};

ItemProperties.propTypes = {
    /** props for the ClassificationProperty component */
    classificationProps: PropTypes.shape({
        /** function that opens the classification modal, should only be set if user can edit classification */
        openModal: PropTypes.func,
        /** the tooltip to show for the banner policy body if it exists */
        tooltip: PropTypes.string,
        /** the classification value shown as a badge if an item has classification */
        value: PropTypes.string
    }),
    /** the datetime this item was created, accepts any value that can be passed to the Date() constructor */
    createdAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** a description for the item */
    description: PropTypes.string,
    /** props for the editable description textarea */
    descriptionTextareaProps: PropTypes.object,
    /** the name of the item's enterprise owner if the item is owned by a different enterprise */
    enterpriseOwner: PropTypes.string,
    /** the datetime this item was last modified, accepts any value that can be passed to the Date() constructor */
    modifiedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** function called on description textarea blur with the new value to save */
    onDescriptionChange: PropTypes.func,
    /** function called on url input blur with the new value to save */
    onValidURLChange: PropTypes.func,
    /** the name of the item's owner */
    owner: PropTypes.string,
    /** props for the retention policy of this item */
    retentionPolicyProps: PropTypes.object,
    /** use the getFileSize util to get a localized human-readable string from the number of bytes */
    size: PropTypes.string,
    /** the datetime this item was deleted or moved to trash, accepts any value that can be passed to the Date() constructor */
    trashedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    /** the name of the user who uploaded this item */
    uploader: PropTypes.string,
    /** the URL for the item when the item is a weblink */
    url: PropTypes.string
};

export default ItemProperties;